import * as React from 'react';
import { cn } from 'cn';

// Substitutos do Row/Col do reactstrap, com a mesma API, para a troca ser só
// de import. Reproduzem o grid que as telas têm hoje: Bootstrap 5 por cima do
// 4.5 do modern.css (de onde vem o position: relative das colunas), gutter de
// 24px e os breakpoints do Bootstrap (576/768/992/1200/1440), não os do
// Tailwind. Todos como min-[...]: o Tailwind emite os arbitrários antes dos
// nomeados, e um md: nomeado venceria o lg no desktop.
//
// O gutter vem de --bs-gutter-x/-y, como no Bootstrap, para as classes g-*,
// gx-*, gy-* e no-gutters das telas continuarem valendo. O valor padrão fica
// em :where() para essas classes vencerem.
//
// Sem data-slot: o reset de tailwind-base.css zera margin e padding com a
// mesma especificidade das classes legadas que as telas passam em className.
// O data-grid é o gancho do CSS legado que mirava .row e [class*='col-'].

type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

type ColumnSize = boolean | 'auto' | number | string;

type ColumnProp = ColumnSize | { size?: ColumnSize; offset?: number | string };

const breakpoints: Breakpoint[] = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'];

// Tabelas com as classes escritas por extenso: o Tailwind só gera o que
// encontra literal no código, então nada de montar `w-${n}/12`.
const sizeClasses: Record<Breakpoint, Record<string, string>> = {
  xs: {
    true: 'tw:flex-[1_0_0px]',
    auto: 'tw:flex-none tw:w-auto',
    1: 'tw:flex-none tw:w-1/12',
    2: 'tw:flex-none tw:w-2/12',
    3: 'tw:flex-none tw:w-3/12',
    4: 'tw:flex-none tw:w-4/12',
    5: 'tw:flex-none tw:w-5/12',
    6: 'tw:flex-none tw:w-6/12',
    7: 'tw:flex-none tw:w-7/12',
    8: 'tw:flex-none tw:w-8/12',
    9: 'tw:flex-none tw:w-9/12',
    10: 'tw:flex-none tw:w-10/12',
    11: 'tw:flex-none tw:w-11/12',
    12: 'tw:flex-none tw:w-full',
  },
  sm: {
    true: 'tw:min-[576px]:flex-[1_0_0px]',
    auto: 'tw:min-[576px]:flex-none tw:min-[576px]:w-auto',
    1: 'tw:min-[576px]:flex-none tw:min-[576px]:w-1/12',
    2: 'tw:min-[576px]:flex-none tw:min-[576px]:w-2/12',
    3: 'tw:min-[576px]:flex-none tw:min-[576px]:w-3/12',
    4: 'tw:min-[576px]:flex-none tw:min-[576px]:w-4/12',
    5: 'tw:min-[576px]:flex-none tw:min-[576px]:w-5/12',
    6: 'tw:min-[576px]:flex-none tw:min-[576px]:w-6/12',
    7: 'tw:min-[576px]:flex-none tw:min-[576px]:w-7/12',
    8: 'tw:min-[576px]:flex-none tw:min-[576px]:w-8/12',
    9: 'tw:min-[576px]:flex-none tw:min-[576px]:w-9/12',
    10: 'tw:min-[576px]:flex-none tw:min-[576px]:w-10/12',
    11: 'tw:min-[576px]:flex-none tw:min-[576px]:w-11/12',
    12: 'tw:min-[576px]:flex-none tw:min-[576px]:w-full',
  },
  md: {
    true: 'tw:min-[768px]:flex-[1_0_0px]',
    auto: 'tw:min-[768px]:flex-none tw:min-[768px]:w-auto',
    1: 'tw:min-[768px]:flex-none tw:min-[768px]:w-1/12',
    2: 'tw:min-[768px]:flex-none tw:min-[768px]:w-2/12',
    3: 'tw:min-[768px]:flex-none tw:min-[768px]:w-3/12',
    4: 'tw:min-[768px]:flex-none tw:min-[768px]:w-4/12',
    5: 'tw:min-[768px]:flex-none tw:min-[768px]:w-5/12',
    6: 'tw:min-[768px]:flex-none tw:min-[768px]:w-6/12',
    7: 'tw:min-[768px]:flex-none tw:min-[768px]:w-7/12',
    8: 'tw:min-[768px]:flex-none tw:min-[768px]:w-8/12',
    9: 'tw:min-[768px]:flex-none tw:min-[768px]:w-9/12',
    10: 'tw:min-[768px]:flex-none tw:min-[768px]:w-10/12',
    11: 'tw:min-[768px]:flex-none tw:min-[768px]:w-11/12',
    12: 'tw:min-[768px]:flex-none tw:min-[768px]:w-full',
  },
  lg: {
    true: 'tw:min-[992px]:flex-[1_0_0px]',
    auto: 'tw:min-[992px]:flex-none tw:min-[992px]:w-auto',
    1: 'tw:min-[992px]:flex-none tw:min-[992px]:w-1/12',
    2: 'tw:min-[992px]:flex-none tw:min-[992px]:w-2/12',
    3: 'tw:min-[992px]:flex-none tw:min-[992px]:w-3/12',
    4: 'tw:min-[992px]:flex-none tw:min-[992px]:w-4/12',
    5: 'tw:min-[992px]:flex-none tw:min-[992px]:w-5/12',
    6: 'tw:min-[992px]:flex-none tw:min-[992px]:w-6/12',
    7: 'tw:min-[992px]:flex-none tw:min-[992px]:w-7/12',
    8: 'tw:min-[992px]:flex-none tw:min-[992px]:w-8/12',
    9: 'tw:min-[992px]:flex-none tw:min-[992px]:w-9/12',
    10: 'tw:min-[992px]:flex-none tw:min-[992px]:w-10/12',
    11: 'tw:min-[992px]:flex-none tw:min-[992px]:w-11/12',
    12: 'tw:min-[992px]:flex-none tw:min-[992px]:w-full',
  },
  xl: {
    true: 'tw:min-[1200px]:flex-[1_0_0px]',
    auto: 'tw:min-[1200px]:flex-none tw:min-[1200px]:w-auto',
    1: 'tw:min-[1200px]:flex-none tw:min-[1200px]:w-1/12',
    2: 'tw:min-[1200px]:flex-none tw:min-[1200px]:w-2/12',
    3: 'tw:min-[1200px]:flex-none tw:min-[1200px]:w-3/12',
    4: 'tw:min-[1200px]:flex-none tw:min-[1200px]:w-4/12',
    5: 'tw:min-[1200px]:flex-none tw:min-[1200px]:w-5/12',
    6: 'tw:min-[1200px]:flex-none tw:min-[1200px]:w-6/12',
    7: 'tw:min-[1200px]:flex-none tw:min-[1200px]:w-7/12',
    8: 'tw:min-[1200px]:flex-none tw:min-[1200px]:w-8/12',
    9: 'tw:min-[1200px]:flex-none tw:min-[1200px]:w-9/12',
    10: 'tw:min-[1200px]:flex-none tw:min-[1200px]:w-10/12',
    11: 'tw:min-[1200px]:flex-none tw:min-[1200px]:w-11/12',
    12: 'tw:min-[1200px]:flex-none tw:min-[1200px]:w-full',
  },
  xxl: {
    true: 'tw:min-[1440px]:flex-[1_0_0px]',
    auto: 'tw:min-[1440px]:flex-none tw:min-[1440px]:w-auto',
    1: 'tw:min-[1440px]:flex-none tw:min-[1440px]:w-1/12',
    2: 'tw:min-[1440px]:flex-none tw:min-[1440px]:w-2/12',
    3: 'tw:min-[1440px]:flex-none tw:min-[1440px]:w-3/12',
    4: 'tw:min-[1440px]:flex-none tw:min-[1440px]:w-4/12',
    5: 'tw:min-[1440px]:flex-none tw:min-[1440px]:w-5/12',
    6: 'tw:min-[1440px]:flex-none tw:min-[1440px]:w-6/12',
    7: 'tw:min-[1440px]:flex-none tw:min-[1440px]:w-7/12',
    8: 'tw:min-[1440px]:flex-none tw:min-[1440px]:w-8/12',
    9: 'tw:min-[1440px]:flex-none tw:min-[1440px]:w-9/12',
    10: 'tw:min-[1440px]:flex-none tw:min-[1440px]:w-10/12',
    11: 'tw:min-[1440px]:flex-none tw:min-[1440px]:w-11/12',
    12: 'tw:min-[1440px]:flex-none tw:min-[1440px]:w-full',
  },
};

const offsetClasses: Record<Breakpoint, Record<string, string>> = {
  xs: {
    0: 'tw:ml-0',
    1: 'tw:ml-[8.333333%]',
    2: 'tw:ml-[16.666667%]',
    3: 'tw:ml-[25%]',
    4: 'tw:ml-[33.333333%]',
    5: 'tw:ml-[41.666667%]',
    6: 'tw:ml-[50%]',
    7: 'tw:ml-[58.333333%]',
    8: 'tw:ml-[66.666667%]',
    9: 'tw:ml-[75%]',
    10: 'tw:ml-[83.333333%]',
    11: 'tw:ml-[91.666667%]',
  },
  sm: {
    0: 'tw:min-[576px]:ml-0',
    1: 'tw:min-[576px]:ml-[8.333333%]',
    2: 'tw:min-[576px]:ml-[16.666667%]',
    3: 'tw:min-[576px]:ml-[25%]',
    4: 'tw:min-[576px]:ml-[33.333333%]',
    5: 'tw:min-[576px]:ml-[41.666667%]',
    6: 'tw:min-[576px]:ml-[50%]',
    7: 'tw:min-[576px]:ml-[58.333333%]',
    8: 'tw:min-[576px]:ml-[66.666667%]',
    9: 'tw:min-[576px]:ml-[75%]',
    10: 'tw:min-[576px]:ml-[83.333333%]',
    11: 'tw:min-[576px]:ml-[91.666667%]',
  },
  md: {
    0: 'tw:min-[768px]:ml-0',
    1: 'tw:min-[768px]:ml-[8.333333%]',
    2: 'tw:min-[768px]:ml-[16.666667%]',
    3: 'tw:min-[768px]:ml-[25%]',
    4: 'tw:min-[768px]:ml-[33.333333%]',
    5: 'tw:min-[768px]:ml-[41.666667%]',
    6: 'tw:min-[768px]:ml-[50%]',
    7: 'tw:min-[768px]:ml-[58.333333%]',
    8: 'tw:min-[768px]:ml-[66.666667%]',
    9: 'tw:min-[768px]:ml-[75%]',
    10: 'tw:min-[768px]:ml-[83.333333%]',
    11: 'tw:min-[768px]:ml-[91.666667%]',
  },
  lg: {
    0: 'tw:min-[992px]:ml-0',
    1: 'tw:min-[992px]:ml-[8.333333%]',
    2: 'tw:min-[992px]:ml-[16.666667%]',
    3: 'tw:min-[992px]:ml-[25%]',
    4: 'tw:min-[992px]:ml-[33.333333%]',
    5: 'tw:min-[992px]:ml-[41.666667%]',
    6: 'tw:min-[992px]:ml-[50%]',
    7: 'tw:min-[992px]:ml-[58.333333%]',
    8: 'tw:min-[992px]:ml-[66.666667%]',
    9: 'tw:min-[992px]:ml-[75%]',
    10: 'tw:min-[992px]:ml-[83.333333%]',
    11: 'tw:min-[992px]:ml-[91.666667%]',
  },
  xl: {
    0: 'tw:min-[1200px]:ml-0',
    1: 'tw:min-[1200px]:ml-[8.333333%]',
    2: 'tw:min-[1200px]:ml-[16.666667%]',
    3: 'tw:min-[1200px]:ml-[25%]',
    4: 'tw:min-[1200px]:ml-[33.333333%]',
    5: 'tw:min-[1200px]:ml-[41.666667%]',
    6: 'tw:min-[1200px]:ml-[50%]',
    7: 'tw:min-[1200px]:ml-[58.333333%]',
    8: 'tw:min-[1200px]:ml-[66.666667%]',
    9: 'tw:min-[1200px]:ml-[75%]',
    10: 'tw:min-[1200px]:ml-[83.333333%]',
    11: 'tw:min-[1200px]:ml-[91.666667%]',
  },
  xxl: {
    0: 'tw:min-[1440px]:ml-0',
    1: 'tw:min-[1440px]:ml-[8.333333%]',
    2: 'tw:min-[1440px]:ml-[16.666667%]',
    3: 'tw:min-[1440px]:ml-[25%]',
    4: 'tw:min-[1440px]:ml-[33.333333%]',
    5: 'tw:min-[1440px]:ml-[41.666667%]',
    6: 'tw:min-[1440px]:ml-[50%]',
    7: 'tw:min-[1440px]:ml-[58.333333%]',
    8: 'tw:min-[1440px]:ml-[66.666667%]',
    9: 'tw:min-[1440px]:ml-[75%]',
    10: 'tw:min-[1440px]:ml-[83.333333%]',
    11: 'tw:min-[1440px]:ml-[91.666667%]',
  },
};

// Os filhos seguem o `.row > *` do Bootstrap 5, que vale também para o que não
// é Col; o :where() zera a especificidade para as classes da Col vencerem. O
// .btn fica fora da largura, como no `.row > .btn` de _bs4-utils-compat.
const rowClasses =
  'tw:[:where(&)]:[--bs-gutter-x:24px] tw:[:where(&)]:[--bs-gutter-y:0px] tw:flex tw:flex-wrap tw:mx-[calc(var(--bs-gutter-x)*-0.5)] tw:mt-[calc(var(--bs-gutter-y)*-1)] tw:[:where(&)>*]:shrink-0 tw:[:where(&)>*:not(:where(.btn))]:w-full tw:[:where(&)>*]:max-w-full tw:[:where(&)>*]:px-[calc(var(--bs-gutter-x)*0.5)] tw:[:where(&)>*]:mt-[var(--bs-gutter-y)]';

/**
 * Linha do grid de 12 colunas, com a mesma API do reactstrap. Os filhos são `Col`.
 * @import import { Row, Col } from 'src/componentes/grid';
 */
const Row = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<'div'>
>(function Row({ className, ...props }, ref) {
  return (
    <div
      ref={ref}
      data-grid="row"
      className={cn(rowClasses, className)}
      {...props}
    />
  );
});

type ColProps = React.ComponentPropsWithoutRef<'div'> &
  Partial<Record<Breakpoint, ColumnProp>>;

// Mesmas regras do getColumnClasses do reactstrap: true ou '' é coluna
// flexível e 0/false/undefined não gera nada. Só a coluna sem nenhuma dessas
// props (nem um objeto só com offset) divide o espaço como o `.col`.
function columnClasses(props: Partial<Record<Breakpoint, ColumnProp>>) {
  const classes: string[] = [];

  breakpoints.forEach((bp) => {
    const prop = props[bp];
    if (!prop && prop !== '') return;

    const { size, offset } =
      typeof prop === 'object' ? prop : { size: prop, offset: undefined };

    if (size || size === '') {
      classes.push(
        sizeClasses[bp][size === true || size === '' ? 'true' : String(size)]
      );
    }

    if (offset || offset === 0) {
      classes.push(offsetClasses[bp][String(offset)]);
    }
  });

  return classes;
}

// O reactstrap gerava uma classe col-* para todo tamanho, menos o xs flexível
// (que era só .col). O data-grid-sized marca essas colunas para o CSS legado
// que mirava [class*='col-'].
function hasLegacySizeClass(props: Partial<Record<Breakpoint, ColumnProp>>) {
  return breakpoints.some((bp) => {
    const prop = props[bp];
    const size = prop && typeof prop === 'object' ? prop.size : prop;
    if (!size && size !== '') return false;
    return bp !== 'xs' || (size !== true && size !== '');
  });
}

// O padding cai em 24px fora de uma Row, como o do .col-* do Bootstrap 4.
/**
 * Coluna do grid. `xs`…`xxl` recebem o número de colunas (1 a 12), `'auto'`, `true` (divide o espaço) ou `{ size, offset }`.
 * @import import { Row, Col } from 'src/componentes/grid';
 */
const Col = React.forwardRef<HTMLDivElement, ColProps>(function Col(
  { className, xs, sm, md, lg, xl, xxl, ...props },
  ref
) {
  const hasColumnProp = [xs, sm, md, lg, xl, xxl].some((p) => p || p === '');

  return (
    <div
      ref={ref}
      data-grid="col"
      data-grid-sized={
        hasLegacySizeClass({ xs, sm, md, lg, xl, xxl }) ? '' : undefined
      }
      className={cn(
        'tw:relative tw:w-full tw:max-w-full tw:px-[calc(var(--bs-gutter-x,24px)*0.5)]',
        !hasColumnProp && sizeClasses.xs.true,
        columnClasses({ xs, sm, md, lg, xl, xxl }),
        className
      )}
      {...props}
    />
  );
});

// O filterChildren de componentes/filtro/searchUtils reconhece Row e Col pelo
// nome para buscar dentro deles, e o forwardRef não tem name.
Row.displayName = 'Row';
Col.displayName = 'Col';

export { Row, Col };
